import React, { useEffect, useState } from "react";
import Layout from '../Layout';
import DashboardAPI from '../services/DashboardAPI';
import { useNavigate } from 'react-router-dom';

import {
    BookOpen,
    Calendar,
    ClipboardCheck,
    UserCheck,
    ChevronRight,
    Clock,
    TrendingUp,
    AlertCircle,
    Settings,
    User,
    Bell
} from "lucide-react";

const HomePage = () => {
    const navigate = useNavigate();

    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profile, setProfile] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [events, setEvents] = useState([]);
    const [attendance, setAttendance] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('[Dashboard] Fetching comprehensive dashboard data...');

            // Call NEW comprehensive endpoint - ONE call instead of 5!
            const data = await DashboardAPI.getDashboardOverview();

            console.log('[Dashboard] Overview data:', data);

            setProfile(data.profile);
            setLessons(data.lessons || []);
            setAssessments(data.assessments || []);
            setEvents(data.events || []);

            // Map attendance summary
            setAttendance({
                overall_attendance: data.attendance_summary?.attendance_percentage || 0,
                total_days: data.attendance_summary?.total_days || 0,
                total_present: data.attendance_summary?.present_days || 0,
                total_absent: data.attendance_summary?.absent_days || 0,
                current_month_attendance: data.attendance_summary?.current_month_attendance || 0,
                last_30_days_attendance: data.attendance_summary?.last_30_days_attendance || 0
            });

        } catch (err) {
            console.error('[Dashboard] Error:', err);
            setError('Failed to load dashboard. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="min-h-screen bg-[#F7FAFF] font-[Poppins,sans-serif] flex items-center justify-center pb-24">
                    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet" />
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F17C45] mb-4"></div>
                        <p className="text-gray-600">Loading your dashboard...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="min-h-screen bg-[#F7FAFF] font-[Poppins,sans-serif] flex items-center justify-center pb-24">
                    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet" />
                    <div className="text-center">
                        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                        <p className="text-red-600 mb-4">{error}</p>
                        <button
                            onClick={fetchDashboardData}
                            className="px-6 py-2 bg-[#F17C45] text-white rounded-lg hover:bg-orange-600 transition font-semibold"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    const studentName = profile?.name || 'Student';
    const studentEmail = profile?.email || '';
    const studentGrade = profile?.grade || 'N/A';
    const studentXP = profile?.xp || 0;

    return (
        <Layout>
            <div className="min-h-screen bg-[#F7FAFF] font-[Poppins,sans-serif] pb-24 relative">
                <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet" />

                {/* Header */}
                <div className="max-w-7xl mx-auto px-4 pt-8 pb-6 flex flex-wrap justify-between items-center">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-[#2173B7]">
                            Hi, Dear {studentName} <span className="hidden sm:inline">👋</span>
                        </h1>
                        <p className="text-[#B97B39] mt-1">Welcome back, let's build national assets together.</p>
                        <p className="text-gray-500 text-sm">{studentEmail}</p>
                    </div>

                    <div className="flex items-center gap-4 mt-4 sm:mt-0">
                        <button
                            className="w-10 h-10 rounded-full bg-white border-2 border-[#F17C45] text-[#F17C45] flex items-center justify-center hover:bg-orange-50 transition-colors"
                            aria-label="Settings"
                        >
                            <Settings size={20} />
                        </button>

                        <button
                            className="w-10 h-10 rounded-full bg-white border-2 border-[#2173B7] text-[#2173B7] flex items-center justify-center hover:bg-blue-50 transition-colors"
                            aria-label="User Profile"
                            onClick={() => navigate('/profile')}
                        >
                            <User size={20} />
                        </button>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="bg-[#FFF8F4] border-2 border-[#F7D1B6] rounded-2xl p-5 text-center">
                            <div className="text-3xl font-bold text-[#F17C45]">{studentGrade}</div>
                            <div className="text-sm text-[#B97B39] mt-1">Grade</div>
                        </div>

                        <div className="bg-[#FFF8F4] border-2 border-[#F7D1B6] rounded-2xl p-5 text-center">
                            <div className="text-3xl font-bold text-[#2173B7]">{lessons.length}</div>
                            <div className="text-sm text-[#B97B39] mt-1">Classes</div>
                        </div>

                        <div className="bg-[#FFF8F4] border-2 border-[#F7D1B6] rounded-2xl p-5 text-center">
                            <div className="text-3xl font-bold text-[#F59E0B]">{studentXP}</div>
                            <div className="text-sm text-[#B97B39] mt-1">XP Points</div>
                        </div>

                        <div className="bg-[#FFF8F4] border-2 border-[#F7D1B6] rounded-2xl p-5 text-center">
                            <div className="text-3xl font-bold text-[#F97316]">{attendance?.overall_attendance || 0}%</div>
                            <div className="text-sm text-[#B97B39] mt-1">Attendance</div>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* My Lessons */}
                        <div className="bg-white border-2 border-[#F7D1B6] rounded-2xl p-6 flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-[#F17C45] flex items-center">
                                    <BookOpen className="mr-2" size={24} />
                                    My Lessons
                                </h2>
                                <div className="flex items-center">
                                    <span className="text-2xl font-bold text-[#F17C45] mr-2">{lessons.length}</span>
                                    <span className="text-sm text-gray-500">Available</span>
                                </div>
                            </div>

                            <div className="space-y-3 flex-1">
                                {lessons.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <BookOpen size={48} className="mx-auto mb-2 text-gray-300" />
                                        <p>No lessons available</p>
                                    </div>
                                ) : (
                                    lessons.slice(0, 3).map((lesson, idx) => (
                                        <div key={idx} className="bg-[#FFF8F4] border border-[#F7D1B6] rounded-xl p-4 hover:shadow-md transition-shadow">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <BookOpen size={16} className="text-[#F17C45]" />
                                                        <h3 className="font-bold text-[#2173B7]">
                                                            {lesson.setup?.title || lesson.title || 'Lesson'}
                                                        </h3>
                                                    </div>
                                                    <p className="text-sm text-[#B97B39] mt-1">
                                                        {lesson.setup?.domain || lesson.domain || 'General'} • {lesson.setup?.grade || lesson.grade || studentGrade}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <button
                                onClick={() => navigate('/my-lessons')}
                                className="w-full py-2 bg-[#F17C45] text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center mt-4"
                            >
                                View All Lessons
                                <ChevronRight size={18} className="ml-1" />
                            </button>
                        </div>

                        {/* My Assessments */}
                        <div className="bg-white border-2 border-[#F7D1B6] rounded-2xl p-6 flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-[#F17C45] flex items-center">
                                    <ClipboardCheck className="mr-2" size={24} />
                                    My Assessments
                                </h2>
                                <div className="flex items-center">
                                    <span className="text-2xl font-bold text-[#F17C45] mr-2">{assessments.length}</span>
                                    <span className="text-sm text-gray-500">Total</span>
                                </div>
                            </div>

                            <div className="space-y-3 flex-1">
                                {assessments.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <ClipboardCheck size={48} className="mx-auto mb-2 text-gray-300" />
                                        <p>No assessments assigned</p>
                                    </div>
                                ) : (
                                    assessments.slice(0, 3).map((assessment, idx) => (
                                        <div key={idx} className="bg-[#FFF8F4] border border-[#F7D1B6] rounded-xl p-4 hover:shadow-md transition-shadow">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <ClipboardCheck size={16} className="text-[#F17C45]" />
                                                        <h3 className="font-bold text-[#2173B7]">
                                                            {assessment.title || assessment.setup?.title || 'Assessment'}
                                                        </h3>
                                                    </div>
                                                    <p className="text-sm text-[#B97B39] mt-1">
                                                        {assessment.domain || assessment.setup?.domain || 'General'}
                                                    </p>
                                                    {assessment.submission_status && (
                                                        <span className={`inline-block text-xs px-2 py-1 rounded mt-2 ${assessment.submission_status === 'submitted' || assessment.submission_status === 'approved'
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-orange-100 text-orange-700'
                                                            }`}>
                                                            {assessment.submission_status.replace('_', ' ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <button
                                onClick={() => navigate('/assessments')}
                                className="w-full py-2 bg-[#F17C45] text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center mt-4"
                            >
                                View All Assessments
                                <ChevronRight size={18} className="ml-1" />
                            </button>
                        </div>

                        {/* My Events */}
                        <div className="bg-white border-2 border-[#F7D1B6] rounded-2xl p-6 flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-[#F17C45] flex items-center">
                                    <Calendar className="mr-2" size={24} />
                                    My Events
                                </h2>
                                <div className="flex items-center">
                                    <span className="text-2xl font-bold text-[#F17C45] mr-2">{events.length}</span>
                                    <span className="text-sm text-gray-500">Scheduled</span>
                                </div>
                            </div>

                            <div className="space-y-3 flex-1">
                                {events.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <Calendar size={48} className="mx-auto mb-2 text-gray-300" />
                                        <p>No upcoming events</p>
                                    </div>
                                ) : (
                                    events.slice(0, 3).map((event, idx) => (
                                        <div key={idx} className="bg-[#FFF8F4] border border-[#F7D1B6] rounded-xl p-4 hover:shadow-md transition-shadow">
                                            <div className="flex items-start gap-3">
                                                <Calendar size={16} className="text-[#F17C45] mt-1" />
                                                <div className="flex-1">
                                                    <h3 className="font-bold text-[#F17C45]">{event.title}</h3>
                                                    <p className="text-sm text-[#B97B39] mt-1">
                                                        {new Date(event.start_date).toLocaleDateString()} • {event.event_type}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <button
                                onClick={() => navigate('/my-calendar')}
                                className="w-full py-2 bg-[#F17C45] text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center mt-4"
                            >
                                View Calendar
                                <ChevronRight size={18} className="ml-1" />
                            </button>
                        </div>

                        {/* My Attendance */}
                        <div className="bg-white border-2 border-[#F7D1B6] rounded-2xl p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-[#F97316] flex items-center">
                                    <UserCheck className="mr-2" size={24} />
                                    My Attendance
                                </h2>
                                <div className="flex items-center">
                                    <span className="text-2xl font-bold text-[#F97316] mr-2">
                                        {attendance?.overall_attendance || 0}%
                                    </span>
                                    <span className="text-sm text-gray-500">Overall</span>
                                </div>
                            </div>

                            {/* Monthly Calendar */}
                            <div className="mb-4">
                                <div className="text-sm font-bold text-[#2173B7] mb-3">
                                    {new Date().toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                                </div>

                                {/* Calendar Grid */}
                                <div className="grid grid-cols-7 gap-1 text-center">
                                    {/* Day headers */}
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                        <div key={day} className="text-xs font-semibold text-gray-500 py-1">
                                            {day}
                                        </div>
                                    ))}

                                    {/* Calendar days */}
                                    {(() => {
                                        const today = new Date();
                                        const year = today.getFullYear();
                                        const month = today.getMonth();
                                        const firstDay = new Date(year, month, 1).getDay();
                                        const daysInMonth = new Date(year, month + 1, 0).getDate();
                                        const currentDate = today.getDate();

                                        const days = [];

                                        // Empty cells before first day
                                        for (let i = 0; i < firstDay; i++) {
                                            days.push(<div key={`empty-${i}`} className="py-1"></div>);
                                        }

                                        // Actual days - mark first attendance?.total_present days as present
                                        const presentDays = attendance?.total_present || 0;
                                        for (let day = 1; day <= daysInMonth; day++) {
                                            const isToday = day === currentDate;
                                            const isPresent = day <= presentDays && day <= currentDate; // Simple logic: first N days are present

                                            days.push(
                                                <div
                                                    key={day}
                                                    className={`py-1 text-sm rounded-full w-7 h-7 flex items-center justify-center mx-auto ${isToday
                                                        ? 'bg-[#2173B7] text-white font-bold'
                                                        : isPresent
                                                            ? 'bg-[#F97316] text-white'
                                                            : day > currentDate
                                                                ? 'text-gray-300'
                                                                : 'text-gray-400'
                                                        }`}
                                                >
                                                    {day}
                                                </div>
                                            );
                                        }

                                        return days;
                                    })()}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                                    <div className="flex items-center gap-1">
                                        <div className="w-3 h-3 rounded-full bg-[#F97316]"></div>
                                        <span className="text-gray-600">Present</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-3 h-3 rounded-full bg-[#2173B7]"></div>
                                        <span className="text-gray-600">Today</span>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="bg-[#FFF8F4] border border-[#F7D1B6] rounded-lg p-2 text-center">
                                    <div className="text-lg font-bold text-[#2173B7]">{attendance?.total_days || 0}</div>
                                    <div className="text-xs text-gray-600">Total</div>
                                </div>
                                <div className="bg-[#FFF8F4] border border-[#F7D1B6] rounded-lg p-2 text-center">
                                    <div className="text-lg font-bold text-[#F97316]">{attendance?.total_present || 0}</div>
                                    <div className="text-xs text-gray-600">Present</div>
                                </div>
                                <div className="bg-[#FFF8F4] border border-[#F7D1B6] rounded-lg p-2 text-center">
                                    <div className="text-lg font-bold text-[#E63946]">{attendance?.total_absent || 0}</div>
                                    <div className="text-xs text-gray-600">Absent</div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/my-attendance')}
                                className="w-full py-2 bg-[#F97316] text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center"
                            >
                                View Attendance Details
                                <ChevronRight size={18} className="ml-1" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default HomePage;